import React, { useEffect, useState } from "react";
import { api } from "../../lib/api";
import { toast } from "sonner";
import { formatApiError } from "../../lib/errors";
import { Section, Kpi, CategoryChip, AsOfPicker, EmptyRealizedNotice,
         fmtMoney0, fmtMoney2, fmtPct, fmtNum, todayIso } from "./_shared";

export default function FinanceUnitEconomics() {
  const [asOf, setAsOf] = useState(todayIso());
  const [data, setData] = useState(null);
  const [dq, setDq] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    (async () => {
      setLoading(true);
      try {
        const [{ data: ue }, { data: quality }] = await Promise.all([
          api.get("/finance/v2/unit-economics", { params: { as_of: asOf }}),
          api.get("/finance/v2/data-quality"),
        ]);
        setData(ue); setDq(quality);
      } catch (e) { toast.error("Load failed: " + formatApiError(e)); }
      finally { setLoading(false); }
    })();
  }, [asOf]);

  if (loading && !data) return <div className="p-10 text-[#6B7280]" data-testid="unit-econ-loading">Loading…</div>;
  if (!data) return null;
  const a = data.averages; const r = data.realized; const p = data.population;
  const hasRealized = p.simple_realized_cohort > 0;
  const denom = dq?.reliable_realized_cohort?.denominator_disclosure
    || (p.loans_with_valid_duration != null && p.simple_realized_cohort
      ? `${p.loans_with_valid_duration} of ${p.simple_realized_cohort} realized loans with reliable settlement dates`
      : null);

  return (
    <div className="p-6 md:p-8 max-w-[1500px] mx-auto" data-testid="unit-economics">
      <div className="flex flex-wrap items-end justify-between gap-3 mb-6">
        <div>
          <div className="uppercase-label">Finance V2 · preview</div>
          <h1 className="font-display text-3xl md:text-4xl font-semibold tracking-tight text-[#0A2540]">Unit Economics</h1>
          <div className="text-sm text-[#6B7280] mt-1.5">{p.loans_total} loans in scope · simple realized cohort: {p.simple_realized_cohort}</div>
        </div>
        <AsOfPicker value={asOf} onChange={setAsOf} testid="unit-econ-as-of" />
      </div>

      <Section title="Averages" category="mixed" subtitle="Contractual on the whole book; realized only on simple-cohort settled loans.">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
          <Kpi label="Avg original advance" value={fmtMoney2(a.avg_original_advance)} testid="ue-avg-original" />
          <Kpi label="Avg total deployed" value={fmtMoney2(a.avg_total_deployed)} testid="ue-avg-deployed"
               sub="Includes vested additional advances" />
          <Kpi label="Avg case duration" value={a.avg_case_duration_days != null ? `${a.avg_case_duration_days.toFixed(1)} days` : "—"} testid="ue-avg-duration"
               sub="Placeholder same-day dates excluded" />
          <Kpi label="Average Repayment" value={fmtMoney2(a.avg_repayment_per_case ?? a.avg_recorded_settlement)} accent testid="ue-avg-repayment"
               sub="Σ recorded collections ÷ realized closed cases (incl. principal + revenue + fees)" />
          <Kpi label="Avg funding revenue per case" value={fmtMoney2(a.avg_funding_revenue_per_case ?? a.avg_realized_funding_revenue)} testid="ue-avg-funding-revenue"
               sub="Σ realized funding revenue ÷ realized closed cases" />
          <Kpi label="Wtd avg years to settle" value={r.wtd_avg_years_to_settle != null ? `${r.wtd_avg_years_to_settle} yrs` : "—"} testid="ue-wtd-years" />
        </div>
      </Section>

      <Section title="Realized returns" category="realized"
               subtitle={hasRealized ? `Simple cohort: ${p.simple_realized_cohort} loans, $${fmtNum(Math.round(r.sum_deployed_simple_cohort))} deployed` : "No realized data yet"}>
        {!hasRealized && <EmptyRealizedNotice cnt={p.simple_realized_cohort} />}
        {denom && (
          <div className="rounded-md bg-[#FFFBEB] border border-[#FCD34D] px-3 py-2 mb-3 text-[11px] text-[#78350F]"
               data-testid="ue-denominator-disclosure">
            Duration / IRR / annualized figures below are based on {denom}. Loans with missing or placeholder settlement dates are excluded.
          </div>
        )}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-3">
          <Kpi label="Gross Yield" value={fmtPct(r.gross_yield ?? r.return_on_deployed_capital)} accent testid="ue-gross-yield"
               sub="Realized Funding Revenue ÷ Principal Deployed · pre-loss, pre-cost-of-capital" />
          <Kpi label="MOIC — simple cohort" value={r.moic_simple_cohort != null ? `${r.moic_simple_cohort.toFixed(3)}×` : "—"} testid="ue-moic" />
          <Kpi label="Loss rate / Default rate" value={fmtPct(r.loss_rate_simple_cohort)} negative testid="ue-loss-rate" />
          <Kpi label="Net Return on Deployed (post-loss)" value={fmtPct(r.return_on_deployed_capital)} testid="ue-return-deployed"
               sub="(Σ Revenue − Σ Loss) ÷ Σ Deployed · distinct from Gross Yield" />
          <Kpi label="Annualized return (wtd)" value={fmtPct(r.annualized_return_dollar_weighted)} testid="ue-annualized"
               sub={`MOIC^(1/wtd-avg-years) − 1 · pending final tie-out`} />
          <Kpi label="Median XIRR" value={fmtPct(r.annualized_irr_median)} testid="ue-irr-median"
               sub={r.annualized_irr_count ? `Across ${r.annualized_irr_count} loans · pending final tie-out` : "No data"} />
          <Kpi label="Sum realized revenue" value={fmtMoney0(r.sum_realized_revenue_simple_cohort)} testid="ue-sum-revenue" />
          <Kpi label="Sum principal loss" value={fmtMoney0(r.sum_principal_loss_simple_cohort)} testid="ue-sum-loss" />
        </div>
      </Section>
    </div>
  );
}
